import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteUser, postUser } from "../../helper/http";
import styles from "./TransactionTemplateEditor.module.css";
import { ToastService } from "../../service/toast.service";
import AddElementModal from "../../components/TransactionTemplate/Modals/AddElementModal/AddElementModal";
import ShapeGuideModal from "../../components/TransactionTemplate/Modals/ShapeGuideModal/ShapeGuideModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBook,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import type {
  ProcessTemplate,
  Primitive,
  Step,
  StepConnection,
  EditingElement,
  Module,
} from "../../types/TransactionTemplate.types";
import EditPanel from "../../components/TransactionTemplate/EditPanel/EditPanel";
import ShapeLibraryModal from "../../components/Transactions/ShapeLibraryModal/ShapeLibraryModal";
import DeleteElementModal from "../../components/TransactionTemplate/Modals/DeleteStepConfirmModal/DeleteConfirmModal";
import { useLanguage } from "../../context/LanguageContext";
import TransactionSchema from "./TransactionSchema";
import {
  derivePrimVisuals,
  getComputedColor,
} from "../../utils/transactions/editor.utils";
import { useTransactionKeyboardNavigation } from "../../hooks/transactions/useTransactionKeyboardNavigation";
import {
  buildActionTree,
  convertTemplateToSteps,
  convertTransactionToSteps,
} from "../../utils/transactions/transaction.converters";
import {
  BRANCH_NO_OFFSET,
  BRANCH_YES_OFFSET,
  DEFAULT_START_Y,
  EVENT_X,
  EVENT_Y,
  getShapeConfig,
  getShapeType,
  PRIMITIVE_GAP,
  Y_THRESHOLD,
} from "../../types/editor.constants";
import {
  computePlacement,
  resolveBranchConnection,
} from "../../utils/transactions/shape.placement";
import {
  loadCollapsed,
  saveCollapsed,
} from "../../utils/transactions/collapse.utils";
import {
  DropTarget,
  getLogicalBlock,
  useDragPrimitive,
} from "../../hooks/transactions/useDragPrimitive";
import { computeSwappedStep } from "../../utils/transactions/swapPrimitives";
import { computeMovedStep } from "../../utils/transactions/movePrimitive";

interface Transaction {
  id: number;
  actions: any[] | Record<string, any>;
  queues: any;
}

interface DeleteModalState {
  isOpen: boolean;
  type: "primitive" | "transaction" | "step";
  name?: string;
  onConfirm: () => void;
}

export default function TransactionTemplateEditor() {
  const { transactionId } = useParams<{ transactionId: string }>();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const newPrimCoordsRef = useRef<{ x: number; y: number } | null>(null);

  const [templates, setTemplates] = useState<ProcessTemplate[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    number | null
  >(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingElement, setEditingElement] = useState<EditingElement | null>(
    null,
  );
  const [modules, setModules] = useState<Module[]>([]);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const [showShapeGuide, setShowShapeGuide] = useState(false);
  const [stepConnections, setStepConnections] = useState<StepConnection[]>([]);
  const [isShapeLibraryOpen, setIsShapeLibraryOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);
  const [deleteModal, setDeleteModal] = useState<DeleteModalState | null>(null);

  const [collapsedBranches, setCollapsedBranches] = useState<Set<string>>(
    new Set(),
  );

  const [selectedPrimitive, setSelectedPrimitive] = useState<{
    stepIndex: number;
    primIndex: number;
  } | null>(null);

  const [addMode, setAddMode] = useState<{
    afterPrimitive: { stepIndex: number; primIndex: number };
    branch?: "yes" | "no" | "after";
  } | null>(null);

  const [editingPrimitiveText, setEditingPrimitiveText] = useState<{
    stepIndex: number;
    primIndex: number;
    text: string;
  } | null>(null);

  const [isAddPrimitiveOpen, setIsAddPrimitiveOpen] = useState(false);
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [isAddStepOpen, setIsAddStepOpen] = useState(false);
  const [eventStepIndex, setEventStepIndex] = useState<number | null>(null);
  const [primitiveStepIndex, setPrimitiveStepIndex] = useState<number | null>(
    null,
  );

  const suppressEditPanelRef = useRef(false);

  useEffect(() => {
    if (!selectedPrimitive || !svgRef.current) return;

    const step = steps[selectedPrimitive.stepIndex];
    if (!step) return;

    const svgContainer = svgRef.current.parentElement;
    if (!svgContainer) return;

    let x: number, y: number;

    if (selectedPrimitive.primIndex === -1 && step.event) {
      x = step.event.x;
      y = step.y + step.event.y;
    } else {
      const prim = step.primitives[selectedPrimitive.primIndex];
      if (!prim) return;
      x = prim.x;
      y = step.y + prim.y;
    }

    svgContainer.scrollTo({
      left: x - 200,
      top: y - 100,
      behavior: "smooth",
    });
  }, [selectedPrimitive]);

  const primaryColor = "#000000";
  const textColor = "#000000";
  const borderColor = getComputedColor("--border", "#e0e0e0");
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const selectedTransaction = transactions.find(
    (t) => t.id === selectedTransactionId,
  );

  useEffect(() => {
    if (!selectedPrimitive || !isEditMode) return;
    if (suppressEditPanelRef.current) {
      suppressEditPanelRef.current = false;
      return;
    }

    const step = steps[selectedPrimitive.stepIndex];
    if (!step) return;

    if (selectedPrimitive.primIndex === -1 && step.event) {
      setEditingElement({
        type: "event",
        stepIndex: selectedPrimitive.stepIndex,
        primitiveIndex: undefined,
        data: step.event.data || {},
      });
    } else if (selectedPrimitive.primIndex >= 0) {
      const prim = step.primitives[selectedPrimitive.primIndex];
      if (prim) {
        setEditingElement({
          type: "primitive",
          stepIndex: selectedPrimitive.stepIndex,
          primitiveIndex: selectedPrimitive.primIndex,
          data: prim.data || {},
        });
      }
    }
  }, [selectedPrimitive, isEditMode, steps]);

  useEffect(() => {
    loadTransactions();
    loadModules();
  }, []);

  const handleSwapPrimitives = useCallback(
    (
      stepIndex: number,
      draggedId: string,
      targetId: string,
      originalPositions: Map<string, { x: number; y: number }>,
      originalConnections: Step["connections"],
    ) => {
      setSteps((prev) => {
        const newSteps = [...prev];

        const cleanStep: Step = {
          ...newSteps[stepIndex],
          primitives: newSteps[stepIndex].primitives.map((p) => {
            const orig = originalPositions.get(p.id);
            return orig ? { ...p, x: orig.x, y: orig.y } : p;
          }),
          connections: originalConnections.map((c) => ({ ...c })),
        };

        const swapped = computeSwappedStep(
          stepIndex,
          cleanStep,
          draggedId,
          targetId,
          originalPositions,
          originalConnections,
          selectedTransactionId,
          t,
        );

        if (!swapped) {
          newSteps[stepIndex] = cleanStep;
          return newSteps;
        }

        newSteps[stepIndex] = swapped;
        return newSteps;
      });
    },
    [t, selectedTransactionId],
  );

  const handleMovePrimitive = useCallback(
    (
      stepIndex: number,
      draggedId: string,
      dropTarget: DropTarget,
      originalPositions: Map<string, { x: number; y: number }>,
      originalConnections: Step["connections"],
    ) => {
      setSteps((prev) => {
        const newSteps = [...prev];

        const cleanStep: Step = {
          ...newSteps[stepIndex],
          primitives: newSteps[stepIndex].primitives.map((p) => {
            const orig = originalPositions.get(p.id);
            return orig ? { ...p, x: orig.x, y: orig.y } : p;
          }),
          connections: originalConnections.map((c) => ({ ...c })),
        };

        const moved = computeMovedStep(
          stepIndex,
          cleanStep,
          draggedId,
          dropTarget,
          originalPositions,
          originalConnections,
          selectedTransactionId,
          t,
        );

        if (!moved) {
          newSteps[stepIndex] = cleanStep;
          return newSteps;
        }

        newSteps[stepIndex] = moved;
        return newSteps;
      });
    },
    [t, selectedTransactionId],
  );

  const { onPrimMouseDown, onMouseMove, onMouseUp, getIsDragging } =
    useDragPrimitive(
      steps,
      isEditMode,
      setSteps,
      undefined,
      handleMovePrimitive,
    );

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const loadModules = useCallback(async () => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        try {
          const parsed = JSON.parse(localStorage.getItem(key) || "");
          if (parsed.dashboard) {
            const moduleFields = parsed.dashboard.find(
              (item: any) => item.name === "Module Fields" || item.id === 7,
            );
            if (moduleFields?.list) {
              setModules(
                moduleFields.list.map((m: any) => ({ id: m.id, name: m.name })),
              );
            }
            break;
          }
        } catch {
          /*invalid JSON */
        }
      }
    } catch (error) {
      console.error("Error loading modules:", error);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const response = await postUser("crud/list", {
        module: "transaction",
        page: "1",
        per_page: "100",
        sort_direction: "+",
        sort_field: "id",
      });

      if (response.data?.length) {
        const idx = transactionId
          ? response.data.findIndex((item: any) => item.id === +transactionId)
          : 0;
        setTransactions(response.data);
        setSelectedTransactionId(response.data[idx].id);

        const result = convertTransactionToSteps(response.data[idx]);
        setSteps(result.steps);
        setStepConnections(result.stepConnections);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  const handleCreateTransaction = useCallback(async () => {
    try {
      setLoading(true);
      const response = await postUser("crud/upsert", {
        module: "transaction",
        body: { actions: [], queues: {} },
      });

      if (response?.body) {
        const id = response.body.id;
        const newTx: Transaction = { id, actions: [], queues: {} };

        setTransactions((prev) => [...prev, newTx]);
        setSelectedTransactionId(id);
        setSelectedTemplateId(null);

        setSteps([
          {
            id: "step-0",
            name: `Transaction ${id}`,
            event: {
              id: "event-0",
              name: "transaction_start",
              x: EVENT_X,
              y: EVENT_Y,
              data: {},
              from: [],
            },
            primitives: [],
            connections: [],
            y: 50,
            originalData: newTx,
            outgoingStepConnections: [],
          },
        ]);
        setStepConnections([]);
        setIsEditMode(true);
        setCollapsedBranches(new Set());

        setAddMode({
          afterPrimitive: { stepIndex: 0, primIndex: -1 },
          branch: "after",
        });

        setTimeout(() => setIsShapeLibraryOpen(true), 100);

        ToastService.success(t("transactionCreated", { id }));
      }
    } catch {
      ToastService.error(t("transactionCreateFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleDeleteTransaction = useCallback(() => {
    if (!selectedTransactionId) return;
    setDeleteModal({
      isOpen: true,
      type: "transaction",
      name: `Transaction ${selectedTransactionId}`,
      onConfirm: async () => {
        try {
          await deleteUser("crud/delete", {
            id: selectedTransactionId,
            module: "transaction",
          });
          await loadTransactions();
          setSelectedTransactionId(null);
          setSteps([]);
          ToastService.success(t("transactionDeleted"));
        } catch (error) {
          console.error("Error deleting transaction:", error);
          ToastService.error(t("transactionDeleteFailed"));
        }
      },
    });
  }, [selectedTransactionId, loadTransactions, t]);

  const handleTransactionSelect = useCallback(
    (txId: number) => {
      const tx = transactions.find((t) => t.id === txId);
      if (tx) {
        setSelectedTransactionId(txId);
        setSelectedTemplateId(null);
        const result = convertTransactionToSteps(tx);
        setSteps(result.steps);
        setStepConnections(result.stepConnections);
        setIsEditMode(true);
        setSelectedPrimitive(null);
        setEditingElement(null);
        setEditingPrimitiveText(null);
        setAddMode(null);
        setCollapsedBranches(loadCollapsed(txId));
      }
    },
    [transactions],
  );

  const handleToggleCollapse = useCallback(
    (primId: string) => {
      setCollapsedBranches((prev) => {
        const next = new Set(prev);
        if (next.has(primId)) {
          next.delete(primId);
        } else {
          next.add(primId);
        }
        if (selectedTransactionId) {
          saveCollapsed(selectedTransactionId, next);
        }
        return next;
      });
    },
    [selectedTransactionId],
  );

  const handleElementClick = useCallback(
    (
      type: "event" | "primitive" | "step",
      stepIndex: number,
      primitiveIndex?: number,
    ) => {
      if (!isEditMode && type !== "step") {
        return;
      }

      const step = steps[stepIndex];
      let data: any;

      if (type === "event" && step.event) {
        data = step.event.data || {};
        setSelectedPrimitive({ stepIndex, primIndex: -1 });
      } else if (type === "primitive" && primitiveIndex !== undefined) {
        data = step.primitives[primitiveIndex].data || {};
        setSelectedPrimitive({ stepIndex, primIndex: primitiveIndex });
      } else if (type === "step") {
        data = step.originalData || {};
        setSelectedPrimitive(null);
      }

      setEditingElement({ type, stepIndex, primitiveIndex, data });
    },
    [steps, isEditMode],
  );

  const handleSelectShape = useCallback(
    (shapeType: string, shapeName: string) => {
      if (!steps.length) return;

      let targetStepIndex = 0;
      if (addMode && steps[addMode.afterPrimitive.stepIndex]) {
        targetStepIndex = addMode.afterPrimitive.stepIndex;
      }

      const { visualType, initialData } = getShapeConfig(shapeType, shapeName);
      const activeAddMode = addMode;
      const step = steps[targetStepIndex];

      const activeSelected =
        selectedPrimitive &&
        selectedPrimitive.stepIndex === targetStepIndex &&
        (selectedPrimitive.primIndex === -1 ||
          step?.primitives?.[selectedPrimitive.primIndex])
          ? selectedPrimitive
          : null;

      const newPrimId = `prim-${targetStepIndex}-${Date.now()}`;

      setSteps((prev) => {
        const newSteps = [...prev];
        const currentStep = {
          ...newSteps[targetStepIndex],
          primitives: [...newSteps[targetStepIndex].primitives],
          connections: [...newSteps[targetStepIndex].connections],
        };
        newSteps[targetStepIndex] = currentStep;

        // console.log("addMode:", addMode);
        // console.log("selectedPrimitive:", selectedPrimitive);

        const placement = computePlacement(
          currentStep,
          activeAddMode,
          activeSelected,
          targetStepIndex,
        );

        newPrimCoordsRef.current = { x: placement.startX, y: placement.startY };

        const visualToLogical: Record<string, string> = {
          condition: "if",
          loop: "for",
        };
        const logicalType = visualToLogical[shapeType] || shapeType;

        const newPrimitive: Primitive = {
          id: newPrimId,
          name: shapeName,
          x: placement.startX,
          y: placement.startY,
          type: shapeType as Primitive["type"],
          data: {
            ...initialData,
            type: logicalType,
          },
        };
        // console.log("newPrimitive position:", {
        //   x: newPrimitive.x,
        //   y: newPrimitive.y,
        // });
        // console.log(
        //   "all primitives before:",
        //   currentStep.primitives.map((p) => ({ id: p.id, x: p.x, y: p.y })),
        // );

        currentStep.primitives.push(newPrimitive);

        currentStep.primitives.forEach((p) => {
          if (
            p.id !== newPrimitive.id &&
            Math.abs(p.y - newPrimitive.y) < 30 &&
            p.x >= newPrimitive.x
          ) {
            p.x += PRIMITIVE_GAP;
          }
        });

        if (placement.connectionSourceId) {
          const sourceId = resolveBranchConnection(
            currentStep,
            placement.connectionSourceId,
            placement.isBranching,
            placement.branchType,
            placement.startY,
            newPrimitive.id,
          );
          currentStep.connections.push({
            from: sourceId,
            to: newPrimitive.id,
            type: "primitive",
            branch:
              placement.branchType === "yes" || placement.branchType === "no"
                ? placement.branchType
                : undefined,
          });
        }

        return newSteps;
      });

      setAddMode(null);

      setTimeout(() => {
        setSteps((currentSteps) => {
          const step = currentSteps[targetStepIndex];

          const actions = buildActionTree(step);
          const fakeTransaction = {
            id: selectedTransactionId || 0,
            actions,
            queues: {},
          };
          const recalculated = convertTransactionToSteps(fakeTransaction);
          const recalcStep = recalculated.steps[0];

          const newSteps = [...currentSteps];
          newSteps[targetStepIndex] = {
            ...step,
            primitives: recalcStep.primitives,
            connections: recalcStep.connections,
          };

          const coords = newPrimCoordsRef.current;
          const newIndex = coords
            ? newSteps[targetStepIndex].primitives.findIndex(
                (p) =>
                  Math.abs(p.x - coords.x) < 5 && Math.abs(p.y - coords.y) < 5,
              )
            : -1;
          const newPrim = newSteps[targetStepIndex].primitives[newIndex];

          if (newIndex === -1 || !newPrim) return newSteps;

          setEditingElement({
            type: "primitive",
            stepIndex: targetStepIndex,
            primitiveIndex: newIndex,
            data: newPrim.data,
          });
          setSelectedPrimitive({
            stepIndex: targetStepIndex,
            primIndex: newIndex,
          });

          if (svgRef.current && newPrim) {
            const svgContainer = svgRef.current.parentElement;
            if (svgContainer) {
              svgContainer.scrollTo({
                left: newPrim.x - 200,
                top: step.y + newPrim.y - 100,
                behavior: "smooth",
              });
            }
          }

          return newSteps;
        });
      }, 50);

      ToastService.success(t("added", { name: shapeName }));
    },
    [addMode, selectedPrimitive, steps, t],
  );

  const handleModalDelete = useCallback(() => {
    if (!editingElement) return;
    const step = steps[editingElement.stepIndex];
    const name =
      editingElement.type === "primitive" &&
      editingElement.primitiveIndex !== undefined
        ? step.primitives[editingElement.primitiveIndex].name
        : "";

    setDeleteModal({
      isOpen: true,
      type: "primitive",
      name,
      onConfirm: () => {
        if (!editingElement) return;
        setSteps((prevSteps) => {
          const newSteps = [...prevSteps];
          const step = { ...newSteps[editingElement.stepIndex] };
          if (
            editingElement.type === "primitive" &&
            editingElement.primitiveIndex !== undefined
          ) {
            const deletedId = step.primitives[editingElement.primitiveIndex].id;
            const incomingConn = step.connections.find(
              (c) => c.to === deletedId,
            );
            const outgoingConn = step.connections.find(
              (c) => c.from === deletedId && !c.branch,
            );

            step.primitives = step.primitives.filter(
              (_, i) => i !== editingElement.primitiveIndex,
            );
            step.connections = step.connections.filter(
              (c) => c.from !== deletedId && c.to !== deletedId,
            );

            if (incomingConn && outgoingConn) {
              step.connections.push({
                from: incomingConn.from,
                to: outgoingConn.to,
                type: "primitive",
                branch: incomingConn.branch,
              });
            }
          }

          // console.log(
          //   "primitives after delete:",
          //   step.primitives.map((p) => ({ id: p.id, x: p.x, y: p.y })),
          // );
          // console.log("connections after delete:", step.connections);
          // console.log("actions:", JSON.stringify(buildActionTree(step)));

          const actions = buildActionTree(step);
          const recalculated = convertTransactionToSteps({
            id: selectedTransactionId || 0,
            actions,
            queues: {},
          });
          newSteps[editingElement.stepIndex] = {
            ...step,
            primitives: recalculated.steps[0].primitives,
            connections: recalculated.steps[0].connections,
          };

          return newSteps;
        });
        setEditingElement(null);
        setSelectedPrimitive(null);
        setAddMode(null);
        ToastService.success(t("elementDeleted"));
        setTimeout(() => {
          setSteps((currentSteps) => {
            const actionsArray = currentSteps[0]
              ? buildActionTree(currentSteps[0])
              : [];
            postUser("crud/upsert", {
              id: selectedTransactionId,
              module: "transaction",
              body: {
                actions: actionsArray,
                queues: selectedTransaction?.queues || {},
              },
            })
              .then(() => ToastService.success(t("transactionSaved")))
              .catch(() => ToastService.error(t("transactionSaveError")));
            return currentSteps;
          });
        }, 0);
      },
    });
  }, [editingElement, steps, t]);

  const saveTransaction = useCallback(async () => {
    if (!selectedTransaction) {
      ToastService.info("Please select a template first");
      return;
    }

    try {
      const actionsArray = steps[0] ? buildActionTree(steps[0]) : [];

      const response = await postUser("crud/upsert", {
        id: selectedTransaction.id,
        module: "transaction",
        body: {
          actions: actionsArray,
          queues: selectedTransaction.queues || {},
        },
      });

      ToastService.success(t("transactionSaved"));

      if (response?.body) {
        const updated: Transaction = {
          id: selectedTransaction.id,
          actions: response.body.actions || actionsArray,
          queues: response.body.queues || {},
        };
        setTransactions((prev) =>
          prev.map((tx) => (tx.id === selectedTransaction.id ? updated : tx)),
        );
        setSelectedTransactionId(updated.id);
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      ToastService.error(t("transactionSaveError"));
    }
  }, [selectedTransaction, steps, t]);

  const handleModalSave = useCallback(
    (parsedData: any) => {
      if (!editingElement) return;

      setSteps((prevSteps) => {
        const newSteps = [...prevSteps];
        const step = newSteps[editingElement.stepIndex];

        if (
          editingElement.type === "primitive" &&
          editingElement.primitiveIndex !== undefined
        ) {
          const prim = step.primitives[editingElement.primitiveIndex];
          const logicalType = parsedData.type || "primitive";

          const logicalToVisual: Record<string, string> = {
            if: "condition",
            for: "loop",
            return: "return",
            break: "break",
            throw: "throw",
            function: "function",
            subtransaction: "subtransaction",
            primitive: "primitive",
          };

          prim.data = { ...parsedData, type: logicalType };

          prim.type = (logicalToVisual[logicalType] ||
            logicalType) as Primitive["type"];

          const visuals = derivePrimVisuals(prim.data, prim.name);
          prim.name = visuals.name;
        } else if (editingElement.type === "event") {
          const eventName = parsedData.event || "source_message";
          step.event = {
            id: `event-${editingElement.stepIndex}`,
            name: eventName,
            x: step.event?.x ?? EVENT_X,
            y: step.event?.y ?? EVENT_Y,
            data: parsedData,
            from: [],
          };
        }
        return newSteps;
      });

      suppressEditPanelRef.current = true;
      setEditingElement(null);
      svgRef.current?.focus();

      setTimeout(() => saveTransaction(), 0);
    },
    [editingElement, saveTransaction],
  );

  useTransactionKeyboardNavigation({
    steps,
    isEditMode,
    selectedPrimitive,
    editingPrimitiveText,
    setIsEditMode,
    setSelectedPrimitive,
    setEditingPrimitiveText,
    setAddMode,
    setIsShapeLibraryOpen,
    onCloseEditPanel: () => setEditingElement(null),
    onDeleteSelected: () => {
      if (!selectedPrimitive || selectedPrimitive.primIndex < 0) return;
      const step = steps[selectedPrimitive.stepIndex];
      if (!step) return;
      const prim = step.primitives[selectedPrimitive.primIndex];
      if (!prim) return;
      setDeleteModal({
        isOpen: true,
        type: "primitive",
        name: prim.name,
        onConfirm: () => {
          setSteps((prevSteps) => {
            const newSteps = [...prevSteps];
            const s = { ...newSteps[selectedPrimitive.stepIndex] };
            const deletedId = s.primitives[selectedPrimitive.primIndex].id;
            const incomingConn = s.connections.find((c) => c.to === deletedId);
            const outgoingConn = s.connections.find(
              (c) => c.from === deletedId && !c.branch,
            );

            s.primitives = s.primitives.filter(
              (_, i) => i !== selectedPrimitive.primIndex,
            );
            s.connections = s.connections.filter(
              (c) => c.from !== deletedId && c.to !== deletedId,
            );

            if (incomingConn && outgoingConn) {
              s.connections.push({
                from: incomingConn.from,
                to: outgoingConn.to,
                type: "primitive",
                branch: incomingConn.branch,
              });
            }

            const actions = buildActionTree(s);
            const recalculated = convertTransactionToSteps({
              id: selectedTransactionId || 0,
              actions,
              queues: {},
            });
            newSteps[selectedPrimitive.stepIndex] = {
              ...s,
              primitives: recalculated.steps[0].primitives,
              connections: recalculated.steps[0].connections,
            };

            return newSteps;
          });
          setEditingElement(null);
          setSelectedPrimitive(null);
          setAddMode(null);
          ToastService.success(t("elementDeleted"));
          setTimeout(() => {
            setSteps((currentSteps) => {
              const actionsArray = currentSteps[0]
                ? buildActionTree(currentSteps[0])
                : [];
              postUser("crud/upsert", {
                id: selectedTransactionId,
                module: "transaction",
                body: {
                  actions: actionsArray,
                  queues: selectedTransaction?.queues || {},
                },
              })
                .then(() => ToastService.success(t("transactionSaved")))
                .catch(() => ToastService.error(t("transactionSaveError")));
              return currentSteps;
            });
          }, 0);
        },
      });
    },
    onSave: saveTransaction,
  });

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.templateList}>
        <div className={styles.templateListHeader}>
          <h3>{t("transaction")}</h3>
          <button
            className={styles.iconButtonPrimary}
            onClick={handleCreateTransaction}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>

        {transactions.length > 0 && (
          <>
            <div className={styles.templateItems}>
              {transactions.map((transaction) => {
                let actionsCount = 0;
                if (transaction.actions) {
                  if (Array.isArray(transaction.actions)) {
                    actionsCount = transaction.actions.length;
                  } else {
                    actionsCount = Object.keys(transaction.actions).length;
                  }
                }

                return (
                  <div
                    key={`transaction-${transaction.id}`}
                    className={`${styles.templateItem} ${
                      selectedTransactionId === transaction.id
                        ? styles.templateItemActive
                        : ""
                    }`}
                    onClick={() => handleTransactionSelect(transaction.id)}
                  >
                    <div className={styles.templateItemName}>
                      {t("transaction")} {transaction.id}
                    </div>
                    <div className={styles.templateItemType}>
                      {actionsCount} {t("actions")}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className={styles.editorPanel}>
        <div className={styles.header}>
          <button
            className={styles.iconButton}
            onClick={() => navigate(-1)}
            title={t("back")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <h1>
            {selectedTransaction
              ? `${t("transaction")} ${selectedTransaction.id}`
              : selectedTemplate?.name || t("editor")}
          </h1>

          <div className={styles.headerActions}>
            {/* Режим — оставляем "пилюлей", это важный статус */}
            <button
              className={`${styles.modeToggle} ${isEditMode ? styles.modeEdit : styles.modeView}`}
              onClick={() => setIsEditMode(!isEditMode)}
            >
              <div className={styles.statusDot} />
              <span>{isEditMode ? t("edit") : t("view")}</span>
            </button>

            {isEditMode && (
              <div className={styles.actionGroup}>
                {selectedTransaction && (
                  <button
                    className={styles.iconButtonPrimary}
                    onClick={() => {
                      if (!steps.length) return;
                      setAddMode({
                        afterPrimitive: selectedPrimitive
                          ? {
                              stepIndex: selectedPrimitive.stepIndex,
                              primIndex: selectedPrimitive.primIndex,
                            }
                          : { stepIndex: 0, primIndex: -1 },
                        branch: "after",
                      });
                      setIsShapeLibraryOpen(true);
                    }}
                    title={t("addShape")}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                )}

                {selectedTransaction && (
                  <button
                    className={styles.iconButtonDanger}
                    onClick={handleDeleteTransaction}
                    title={t("deleteTransaction")}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            )}

            <button
              className={styles.iconButton}
              onClick={() => setShowShapeGuide(true)}
              title={t("shapeGuide")}
            >
              <FontAwesomeIcon icon={faBook} />
            </button>
          </div>
        </div>

        <div className={styles.canvas}>
          <TransactionSchema
            steps={steps}
            stepConnections={stepConnections}
            selectedPrimitive={selectedPrimitive}
            editingPrimitiveText={editingPrimitiveText}
            isSelectableTemplate={!!selectedTemplate}
            primaryColor={primaryColor}
            textColor={textColor}
            borderColor={borderColor}
            onElementClick={handleElementClick}
            onBackgroundClick={() => setSelectedPrimitive(null)}
            onDeleteStep={setStepToDelete}
            svgRef={svgRef}
            collapsedBranches={collapsedBranches}
            onToggleCollapse={handleToggleCollapse}
            isEditMode={isEditMode}
            onPrimMouseDown={onPrimMouseDown}
            getIsDragging={getIsDragging}
          />
        </div>
      </div>

      <EditPanel
        isOpen={!!editingElement}
        type={editingElement?.type || "step"}
        data={editingElement?.data || {}}
        onClose={() => setEditingElement(null)}
        onSave={handleModalSave}
        onDelete={
          editingElement?.type === "primitive" ||
          editingElement?.type === "event"
            ? handleModalDelete
            : undefined
        }
        modules={modules}
        isDeleteModalOpen={!!deleteModal}
      />

      {deleteModal && (
        <DeleteElementModal
          isOpen={deleteModal.isOpen}
          elementType={deleteModal.type}
          elementName={deleteModal.name}
          onConfirm={() => {
            deleteModal.onConfirm();
            setDeleteModal(null);
          }}
          onCancel={() => setDeleteModal(null)}
        />
      )}

      <AddElementModal
        isOpen={isAddPrimitiveOpen}
        elementType="primitive"
        steps={steps.map((s) => ({ id: s.id, name: s.name }))}
        selectedStepIndex={primitiveStepIndex ?? 0}
        onStepChange={setPrimitiveStepIndex}
        onClose={() => setIsAddPrimitiveOpen(false)}
        onAdd={(data) => {
          if (primitiveStepIndex === null) return;
          const newPrimIndex = steps[primitiveStepIndex].primitives.length;

          setSteps((prev) => {
            const newSteps = [...prev];
            const step = newSteps[primitiveStepIndex];
            const primName = data.primitive || data.process || "unknown";
            const primType = getShapeType(primName);
            const newPrimitive: Primitive = {
              id: `prim-${primitiveStepIndex}-${newPrimIndex}`,
              name: primName,
              x: 200 + newPrimIndex * 160,
              y: 80,
              type: primType,
              data,
            };

            step.primitives.push(newPrimitive);

            if (newPrimIndex === 0 && step.event) {
              step.connections.push({
                from: step.event.id,
                to: newPrimitive.id,
                type: "primitive",
              });
            } else if (newPrimIndex > 0) {
              step.connections.push({
                from: `prim-${primitiveStepIndex}-${newPrimIndex - 1}`,
                to: newPrimitive.id,
                type: "primitive",
              });
            }

            return newSteps;
          });

          setTimeout(() => {
            setEditingElement({
              type: "primitive",
              stepIndex: primitiveStepIndex,
              primitiveIndex: newPrimIndex,
              data,
            });
            setSelectedPrimitive({
              stepIndex: primitiveStepIndex,
              primIndex: newPrimIndex,
            });
          }, 50);

          setIsAddPrimitiveOpen(false);
          ToastService.success(t("primitiveAdded"));
        }}
      />

      <AddElementModal
        isOpen={isAddEventOpen}
        elementType="event"
        steps={steps.map((s) => ({ id: s.id, name: s.name }))}
        selectedStepIndex={eventStepIndex ?? 0}
        onStepChange={setEventStepIndex}
        onClose={() => setIsAddEventOpen(false)}
        onAdd={(data) => {
          if (eventStepIndex === null) return;
          const step = steps[eventStepIndex];
          if (step.event) {
            ToastService.info(t("alreadyHasEvent"));
            return;
          }
          setEditingElement({ type: "event", stepIndex: eventStepIndex, data });
          setIsAddEventOpen(false);
        }}
      />

      <AddElementModal
        isOpen={isAddStepOpen}
        elementType="step"
        steps={steps.map((s) => ({ id: s.id, name: s.name }))}
        onClose={() => setIsAddStepOpen(false)}
        onAdd={async (data) => {
          if (!selectedTemplate) return;

          const newStepData: any = {
            event: data.event || "source_message",
          };
          if (data.cond) newStepData.cond = data.cond;
          if (data.from && data.from.length > 0) newStepData.from = data.from;

          const initialPrimitive: any = {};
          const primitiveName = data.primitiveName || "process_start";

          if (data.primitiveType === "process") {
            initialPrimitive.process = primitiveName;
            initialPrimitive.type = "process";
          } else {
            initialPrimitive.primitive = primitiveName;
          }

          newStepData.primitives = [initialPrimitive];

          try {
            const updatedTemplate = {
              ...selectedTemplate.template,
              steps: [...(selectedTemplate.template?.steps || []), newStepData],
            };
            await postUser("crud/upsert", {
              id: selectedTemplate.id,
              module: "transaction",
              body: { template: updatedTemplate },
            });
            setTemplates((prev) =>
              prev.map((t) =>
                t.id === selectedTemplate.id
                  ? { ...t, template: updatedTemplate }
                  : t,
              ),
            );
            const result = convertTemplateToSteps({
              ...selectedTemplate,
              template: updatedTemplate,
            });
            setSteps(result.steps);
            setStepConnections(result.stepConnections);
            setIsAddStepOpen(false);
            ToastService.success(t("stepAdded"));
          } catch (e) {
            console.error(e);
            ToastService.error(t("stepAddFailed"));
          }
        }}
      />

      <ShapeGuideModal
        isOpen={showShapeGuide}
        onClose={() => setShowShapeGuide(false)}
        primaryColor={primaryColor}
        textColor={textColor}
      />

      <ShapeLibraryModal
        isOpen={isShapeLibraryOpen}
        onClose={() => {
          setIsShapeLibraryOpen(false);
          setAddMode(null);
        }}
        onSelectShape={handleSelectShape}
      />
    </div>
  );
}
